import { useEffect, useState } from "react";
import ButtonComponent from "../../../../../CommonComponents/common/button/Button";
import './index.css';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, Select, MenuItem, FormControl, Checkbox, ListItemText } from '@mui/material';
import { regionMapping } from "../../constants/mockData";

const ACCESS_LEVEL_OPTIONS = [
  { label: "Bottler Admin", value: "Bottler_Admin", isDisabled: true },
  { label: "Bottler Forecaster", value: "Bottler_Forecaster" },
];

const REGION_OPTIONS = Object.keys(regionMapping ?? {}).map(r => ({ label: r, value: r }));

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const isDomainWhitelisted = (email, domainList) => {
  const parts = email.trim().split("@");
  if (parts.length !== 2) return true; // skip check if email format not yet complete
  const domain = parts[1].toLowerCase();
  return domainList.some(
    (d) =>
      (d.domainName || "").toLowerCase() === domain &&
      (d.status === "ONBOARDED" || d.status === "ACTIVE")
  );
};

const createRow = () => ({
  email: "",
  region: "",
  accessLevel: "",
  bottlerGroups: [],
  rowKey: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
});

const InviteUserModal = ({
  openInviteUserModal,
  handleCloseInviteUserModal,
  handleSubmitInviteUserModal,
  isEditMode,
  editModeData,
  handleUpdateEditDetailsModal,
  submitLoading,
  domainList = [],
}) => {
  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (openInviteUserModal && isEditMode && editModeData) {
      setRows([{
        email: editModeData.userEmail || "",
        region: editModeData.region || "",
        accessLevel: editModeData.role || "",
        bottlerGroups: editModeData.bottlerGroups
          ? Array.isArray(editModeData.bottlerGroups) ? editModeData.bottlerGroups : [editModeData.bottlerGroups]
          : [],
        rowKey: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      }]);
    } else if (openInviteUserModal && !isEditMode) {
      setRows([createRow()]);
    }
  }, [openInviteUserModal, isEditMode, editModeData]);

  const handleClose = () => {
    setRows([createRow()]);
    handleCloseInviteUserModal();
  };

  const handleChange = (rowKey, field, value) => {
    setRows(prev => prev.map(r => {
      if (r.rowKey !== rowKey) return r;
      if (field === "region") return { ...r, [field]: value, bottlerGroups: [] };
      return { ...r, [field]: value };
    }));
  };

  const handleDelete = (rowKey) => {
    setRows(prev => prev.filter(r => r.rowKey !== rowKey));
  };

  const isRowValid = (row) =>
    EMAIL_REGEX.test(row.email.trim()) &&
    row.region &&
    row.accessLevel &&
    row.bottlerGroups.length > 0 &&
    isDomainWhitelisted(row.email, domainList);

  const allValid = rows.length > 0 && rows.every(isRowValid);

  const handleSubmit = () => {
    if (isEditMode) {
      handleUpdateEditDetailsModal(rows[0]);
    } else {
      handleSubmitInviteUserModal(rows);
    }
    setRows([createRow()]);
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const emails = text.split(/[\n,]+/).map(e => e.trim()).filter(Boolean);
      if (!emails.length) return;
      const template = rows[rows.length - 1];
      const seen = new Set(rows.map(r => r.email.toLowerCase()));
      const newRows = emails
        .filter(email => {
          const e = email.toLowerCase();
          if (!EMAIL_REGEX.test(email) || seen.has(e)) return false;
          seen.add(e);
          return true;
        })
        .map(email => ({
          ...template,
          email,
          rowKey: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        }));
      if (newRows.length) setRows(prev => [...prev, ...newRows]);
    } catch (err) {
      console.error("Failed to read clipboard", err);
    }
  };

  const colHeader = { fontWeight: "bold", fontSize: 13 };

  // header labels must align with the flex values of the inputs below
  const colStyle = (flex) => ({ flex, ...colHeader, paddingRight: 4 });

  return (
    <Dialog open={openInviteUserModal} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700 }}>
        {isEditMode ? "Edit User" : "Invite User"}
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <div style={{ padding: "16px 20px", overflowY: "auto", boxSizing: "border-box" }}>
          {/* Column headers */}
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
            <div style={colStyle(2)}>Email <span style={{ color: "red" }}>*</span></div>
            <div style={colStyle(1)}>Region <span style={{ color: "red" }}>*</span></div>
            <div style={colStyle(1)}>Access Level <span style={{ color: "red" }}>*</span></div>
            <div style={colStyle(1)}>Bottler Group <span style={{ color: "red" }}>*</span></div>
            <div style={{ width: 34 }} />
          </div>

          {/* Data rows */}
          {rows.map((row) => (
            <div key={row.rowKey} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 25 }}>
              <div style={{ flex: 2, position: "relative" }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Enter email address"
                  value={row.email}
                  disabled={isEditMode}
                  onChange={e => handleChange(row.rowKey, "email", e.target.value)}
                  error={
                    EMAIL_REGEX.test(row.email.trim()) &&
                    !isDomainWhitelisted(row.email, domainList)
                  }
                  helperText={
                    EMAIL_REGEX.test(row.email.trim()) &&
                    !isDomainWhitelisted(row.email, domainList)
                      ? "Domain not whitelisted"
                      : ""
                  }
                  FormHelperTextProps={{
                    sx: { position: "absolute", top: "100%", mt: "2px", whiteSpace: "nowrap" },
                  }}
                />
              </div>
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  displayEmpty
                  value={row.region}
                  disabled={isEditMode}
                  onChange={e => handleChange(row.rowKey, "region", e.target.value)}
                  renderValue={v => v || <span style={{ color: "#aaa" }}>Select region</span>}
                >
                  {REGION_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  displayEmpty
                  autoComplete="off"
                  value={row.accessLevel}
                  onChange={e => handleChange(row.rowKey, "accessLevel", e.target.value)}
                  renderValue={v =>
                    ACCESS_LEVEL_OPTIONS.find(o => o.value === v && !o.isDisabled)?.label ||
                    <span style={{ color: "#aaa" }}>Select access level</span>
                  }
                >
                  {ACCESS_LEVEL_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value} disabled={o.isDisabled} >{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  multiple
                  autoComplete="off"
                  displayEmpty
                  value={Array.isArray(row.bottlerGroups) ? row.bottlerGroups : []}
                  onChange={e => handleChange(row.rowKey, "bottlerGroups", e.target.value)}
                  renderValue={selected =>
                    selected.length === 0
                      ? <span style={{ color: "#aaa" }}>Select bottler group</span>
                      : (regionMapping[row.region]?.bottlerGroups ?? [])
                          .filter(o => selected.includes(o.value))
                          .map(o => o.label)
                          .join(", ")
                  }
                >
                  {(regionMapping[row.region]?.bottlerGroups ?? []).map(o => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox checked={row.bottlerGroups.includes(o.value)} size="small" />
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => handleDelete(row.rowKey)}
                disabled={rows.length === 1 || isEditMode}
                sx={{ width: 34, height: 34, flexShrink: 0 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          ))}

          {!isEditMode && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              <span
                style={{ color: "#6ac9ce", cursor: "pointer", fontSize: 14, width: "fit-content" }}
                onClick={() => setRows(prev => [...prev, createRow()])}
              >
                + Add another user
              </span>
              <span style={{ fontSize: 12, color: "#555" }}>
                <span
                  style={{ color: "#6ac9ce", cursor: "pointer" }}
                  onClick={handlePasteFromClipboard}
                >
                  Paste Emails from Clipboard
                </span>
                {" (Duplicate/Invalid email id's not allowed)"}
              </span>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <div style={{ display: "flex", gap: 10 }}>
          <ButtonComponent label="Cancel" type="primary" maxWidth="80px" onClick={handleClose} />
          <ButtonComponent
            label={isEditMode ? "Update" : "Add User"}
            type="secondary"
            maxWidth="100px"
            onClick={handleSubmit}
            disabled={!allValid || submitLoading}
          />
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default InviteUserModal;
